import type { ApprovalBoxCounts, ApprovalDocumentSummary, DocumentView, InvolvedStatus } from '../../../types/approval';

/**
 * 결재함 (2026-09-13 · 과제 L · `/approval/box`) — 트리 · 목록 · KPI 가 같이 쓰는 규칙과 **아직 서버에 없는 값**의 자리.
 *
 * 시안 `axi-approval-status` v2(사용자 확인 — 그대로 반영): 원본 approval-management 의 KPI · 결재함 트리 +
 * hr-payroll-approval-admin 의 「결재 대기함」 · 「전자결재 문서」 표.
 *
 * ★ 함은 서버 문서함(`view`) 넷을 **나눠 읽어** 트리 마디로 가른다 — 새 라우트를 만들지 않는다.
 * ★ 데이터가 없는 칸도 **자리는 그린다**(CLAUDE.md 「웹 화면 디자인 개편」) — 아래 `FUTURE_*` 가 그 목록이고,
 *   PR 본문 「후속 서버 요청」 번호와 같다.
 */

/** 트리 마디 — 주소 `?box=` 값과 같다. 옛 주소 넷(`/approval/pending` …)은 L2 에서 여기로 넘긴다. */
export type BoxKey = 'all' | 'wait' | 'progress' | 'done' | 'reject' | 'temp' | 'sent' | 'ref' | 'dept';

export const BOX_KEYS: readonly BoxKey[] = ['all', 'wait', 'progress', 'done', 'reject', 'temp', 'sent', 'ref', 'dept'];

/**
 * 마디 이름 — **사람이 보는 글자의 정본**이다. 여기 말고 딴 데 적지 말 것.
 *
 * ## ⭐⭐ 2026-09-25 — 「참조」 → **「참고」**
 *
 * 2026-09-24 에 결재선의 «자리» 이름을 바꿨다(검토→결재자 · 결재→최종결재자 · 참조→참고자).
 * 그때 **문서함 이름은 일부러 안 바꿨다** — 자리 이름인지 함 이름인지 애매했기 때문이다.
 * 이 판에서 함 이름도 맞췄다: `ref: 참고` · 묶음 「참고 · 열람」.
 *
 * ```
 * 한 PR 로 «함께» 바꾼 자리 — 갈리면 「알림은 참고 · 화면은 참조」가 된다
 *   서버  modules/approval/approval-notify.ts   알림 제목 접두 `참고: …`
 *   웹    이 파일 · box/ApprovalBoxPage.tsx · lib/appr-menu.ts · router/index.tsx
 * ```
 *
 * ⚠ **이미 저장된 알림 행은 옛 글자 그대로다**(`Notification.title` 은 담는 칸이다).
 *   고쳐 쓰지 않는다 — 그때 그 이름으로 보낸 것이 사실이다. **새 알림부터** 「참고:」다.
 * ⚠ 전선 값(`view=referenced` · 줄 종류 `REFER`)은 **안 바꾼다.** 바뀐 것은 사람이 보는 글자뿐이다.
 * ★ 되돌아오는 것은 `ApprovalPage/approval-role-words.guard.test.ts` 가 막는다.
 */
export const BOX_LABEL: Record<BoxKey, string> = {
  all: '전체 문서',
  wait: '결재 대기', progress: '진행 중', done: '완료', reject: '반려',
  temp: '임시', sent: '올린 문서',
  ref: '참고', dept: '부서 결재함',
};

/** 마디의 묶음 — 경로 글자(「결재하기 › 결재 대기」)와 트리 머리가 같이 쓴다. */
export const BOX_GROUP: Record<Exclude<BoxKey, 'all'>, '결재하기' | '기안 문서함' | '참고 · 열람'> = {
  wait: '결재하기', progress: '결재하기', done: '결재하기', reject: '결재하기',
  temp: '기안 문서함', sent: '기안 문서함',
  ref: '참고 · 열람', dept: '참고 · 열람',
};

/**
 * 옛 문서함 주소 넷 → 결재함 마디 (2026-09-13 · 과제 L2 · 사용자 결정 「옛 주소 넷 리다이렉트 · 북마크 보존」).
 * ★ 라우터와 시험이 **같은 표**를 본다 — 한쪽만 바뀌면 북마크가 엉뚱한 마디로 간다.
 * ★ 옛 화면(두 열 목록 + 상세)은 `/approval/legacy/<옛 경로>` 로 그대로 남는다(회귀 대비).
 */
export const LEGACY_BOX_REDIRECTS = {
  pending: 'wait',
  drafts: 'sent',
  temp: 'temp',
  referenced: 'ref',
} as const satisfies Record<string, BoxKey>;

export const boxPath = (box: BoxKey): string => `/approval/box?box=${box}`;

/**
 * 옛 주소의 쿼리 · 해시를 들고 마디로 간다 (2026-09-14 · #1252 리뷰 비차단) — 외부 북마크에 붙은 `?q=…` 가 사라지지 않게.
 * `box` 는 표가 정한 값이 이긴다(옛 주소에 `box=` 가 붙어 있어도 무시).
 */
export function boxPathKeeping(box: BoxKey, search = '', hash = ''): string {
  const rest = new URLSearchParams(search);
  rest.delete('box');
  const tail = rest.toString();
  return `${boxPath(box)}${tail ? `&${tail}` : ''}${hash}`;
}

export function isBoxKey(v: string | null | undefined): v is BoxKey {
  return !!v && (BOX_KEYS as readonly string[]).includes(v);
}

/** 서버 문서함 넷 — 한 번씩 읽어 마디로 가른다. */
export const VIEWS_TO_LOAD: readonly DocumentView[] = ['incoming', 'drafted', 'draft', 'referenced'];

export type BoxSource = Partial<Record<DocumentView, ApprovalDocumentSummary[] | null>>;

const dedupe = (docs: ApprovalDocumentSummary[]): ApprovalDocumentSummary[] => {
  const m = new Map<string, ApprovalDocumentSummary>();
  for (const d of docs) if (!m.has(d.id)) m.set(d.id, d);
  return [...m.values()];
};

/**
 * 마디 → 문서. `null`(그 문서함을 못 읽음)은 빈 배열로 합치되, 전부 못 읽었는지는 `boxLoaded` 가 따로 말한다.
 *
 * - 결재 대기 = 받은 문서 중 **내 차례**(`waiting.mine`) — 결재 홈 · 셸 배지와 같은 선
 * - 진행 중 = 받은 · 올린 문서 중 진행 중이면서 내 차례가 아닌 것
 * - 완료 / 반려 = 받은 · 올린 문서 중 그 상태(회수는 반려 칸이 아니다 — 올린 문서에만 보인다)
 * - 임시 = 아직 안 올린 문서 · 올린 문서 = 내가 올린 것 중 **상신한 것**(서버 drafted 는 임시까지 싣는다) · 참고 = 나를 참고자로 건 문서
 * - 부서 결재함 = **서버에 목록이 없다**(`FUTURE_BOX_REQUESTS` ①) — 빈 상태로 자리만
 */
export function docsForBox(box: BoxKey, src: BoxSource): ApprovalDocumentSummary[] {
  const incoming = src.incoming ?? [];
  const drafted = src.drafted ?? [];
  const draft = src.draft ?? [];
  const referenced = src.referenced ?? [];
  const mine = [...incoming, ...drafted];
  switch (box) {
    case 'all': return dedupe([...incoming, ...drafted, ...draft, ...referenced]);
    case 'wait': return incoming.filter((d) => d.status === 'IN_PROGRESS' && d.waiting?.mine === true);
    case 'progress': return dedupe(mine.filter((d) => d.status === 'IN_PROGRESS' && d.waiting?.mine !== true));
    case 'done': return dedupe(mine.filter((d) => d.status === 'APPROVED'));
    case 'reject': return dedupe(mine.filter((d) => d.status === 'REJECTED'));
    case 'temp': return draft;
    // ★ 서버 `drafted` 는 «내가 기안한 것 전부»라 **임시보관(DRAFT)까지** 싣는다(approval.service · types/approval 주석) —
    //   그대로 두면 임시 문서가 「임시」 와 「올린 문서」 에 두 번 선다(리뷰 #1246). 올린 문서 = 상신한 것만.
    case 'sent': return drafted.filter((d) => d.status !== 'DRAFT');
    case 'ref': return referenced;
    case 'dept': return [];
  }
}

/**
 * 진행 중 · 완료 · 반려 마디 → 서버 `view=involved&status=` (2026-09-14 · 계약 §20.3 「트리 마디 ↔ 목록 대응」).
 * ★ 마디의 수(`counts.inProgress · approved · rejected`)와 누르면 나오는 목록이 **같은 조건**이다.
 * ★ 진행 중에는 **내 차례인 문서도** 선다(배지 「결재 대기」) — 종전 화면 가르기(내 차례 제외)와 다르다. 계약이 정본.
 * ⚠ 옛 서버(`involved` 400)면 `docsForBox` 의 종전 가르기로 선다 — 그때는 서버 셋도 쓰지 않는다(`countFromServer` 의 `involvedOk`).
 */
export const INVOLVED_BOX_STATUS: Partial<Record<BoxKey, InvolvedStatus>> = {
  progress: 'IN_PROGRESS',
  done: 'APPROVED',
  reject: 'REJECTED',
};

/**
 * 서버 함별 건수(§20.3)로 **셀 수 있는 마디만** 값을 준다 — 나머지는 `null`(목록에서 센다).
 * ★ 2026-09-14 — `involvedOk` 이면 진행 중 · 완료 · 반려도 서버 값(`view=involved` 목록과 같은 조건). 기본은 종전대로 `null`.
 * - 결재 대기 = `wait` · 임시 = `draft` · 참고 = `referenced`(같은 이름 문서함과 같은 규칙)
 * - 올린 문서 = `drafted − draft` — 서버 `drafted` 는 임시까지 세고, 이 마디는 상신한 것만이다(리뷰 #1246 · #1247 메모)
 * - 진행 중 · 완료 · 반려 = 서버 셋은 결재선에 있는 남의 문서까지 넓게 센다 → 누른 목록과 수가 갈려 **쓰지 않는다**(`view=involved` 뒤)
 * - 전체 문서 = 문서함끼리 겹침을 서버 수로는 못 뺀다 → 목록에서 · 부서 결재함 = 목록 없음
 */
export function countFromServer(box: BoxKey, counts: ApprovalBoxCounts | null, involvedOk = false): number | null {
  if (!counts) return null;
  if (involvedOk) {
    if (box === 'progress') return counts.inProgress;
    if (box === 'done') return counts.approved;
    if (box === 'reject') return counts.rejected;
  }
  switch (box) {
    case 'wait': return counts.wait;
    case 'temp': return counts.draft;
    case 'sent': return Math.max(0, counts.drafted - counts.draft);
    case 'ref': return counts.referenced;
    default: return null;
  }
}

/** 기안일 — 상신한 날, 임시는 만든 날. */
export const draftedOn = (d: ApprovalDocumentSummary): string => d.draftedAt ?? d.createdAt;

/** `MM-DD` — 목록 칸 폭(시안 v2: 여덟 열이 안 들어가 기안일을 줄였다). */
export function mmdd(iso: string): string {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '—';
  return `${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/** 상태 배지 — 톤은 시안 그대로(대기 = warn · 진행 = pur · 완료 = ok · 반려 = bad · 임시/회수 = slate). */
// 옛 statusBadge() 는 lib/approval-status.ts 로 옮긴 뒤(W10-27) 부르는 곳이 0 이라 지웠다(2026-10-18 · W11b).

/**
 * 후속 서버 요청 — 시안에는 그렸고 서버 응답에 없는 것(PR 본문 번호와 같다).
 * ★ 화면은 이 칸들을 「데이터 준비 중」 · 비활성 단추로 그린다 — 값을 지어내지 않는다.
 */
export const FUTURE_BOX_REQUESTS = [
  '① 부서 결재함 문서 목록 — `GET /approval/dept-boxes` 는 함 이름만 준다(문서 없음)',
  '② (반영 · 서버 #1247 §20.1) 목록 행의 기안자 이름 · 부서 · 사진 — `drafter{id,name,avatarUrl,teamName}`',
  '③ 평균 처리 시간 주별 추이 · 이달 결과별 건수(회수 포함) — `/approval/stats` 는 담당자 전용 · 기간 합계만',
  '④ 일괄 승인 · 일괄 반려 — 지금은 한 건씩(approve/reject)',
  '⑤ (반영 · 서버 #1247 §20.3 + `view=involved&status`) 함별 건수 한 번에 — 진행 중 · 완료 · 반려는 같은 조건 목록과 짝',
  '⑥ (반영 · 서버 #1247 §20.2) 목록 서버 검색(문서번호 · 제목 · 기안자) — `q`',
] as const;

/** 「데이터 준비 중」 한 줄. */
export const NOT_READY_TEXT = '데이터 준비 중';
